import React, { useState, useRef, useEffect } from "react";
import copy from "../../icons/Copy.svg";
import more from "../../icons/more.png";
import newedit from "../../icons/newedit.png";

import TripleDotComponent from "./TripleDotComponent";
import { useNavigate } from "react-router-dom";
import { updateHostProperty } from "../../store/thunks/hostActions";
import { useDispatch } from "react-redux";
import { getEnhancedPropertyData } from "../../utils/propertyDataParser";

const Property = ({ property, data, rawDataList = [] }) => {
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [threeDotBox, setThreeDotBox] = useState(false);
  const [copyText, setCopyText] = useState("Unique Shopping Link");
  const [copyDescriptionText, setCopyDescriptionText] = useState("Message");
  const [editMode, setEditMode] = useState(false);
  const [description, setDescription] = useState("");
  const [commission, setCommission] = useState(0);
  // Get host info from Redux store (not needed for now)
  // const hostInfo = useSelector((state) => state.host.hostInfo);
  
  // DISABLED: Get image URL using the optimized hook with lazy loading
  // const { imageUrl, isLoading } = usePropertyImage(property, hostInfo?._id);
  
  // Temporarily disable image fetching to reduce backend calls
  const imageUrl = null;
  const isLoading = false;

  // Enhanced property data with PMS integration
  const enhancedProperty = getEnhancedPropertyData(property, rawDataList);

  const hasImage = false; // Always use default gradient for now

  // Debug logging
  console.log('Property Component:', {
    propertyId: property._id,
    propertyTitle: property.listingTitle,
    pmsId: property.streamlinePropertyId || property.guestyPropertyId,
    propertySource: property.propertySource,
    imageUrl,
    hasImage,
    isLoading
  });

  const handleKeyUpdate = () => {
    setEditMode(false);
    navigate("/host");
    const propertyCredentials = { description };
    dispatch(updateHostProperty({ pid: property._id, propertyCredentials }));
  };

  // Functionality For Mobile Screens Only
  const handleMore = (e) => {
    setThreeDotBox(!threeDotBox);
    e.stopPropagation();
  };

  function unsecuredCopyToClipboard(text, description) {
    const textArea = document.createElement("textarea");
    textArea.value = description + " " + text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Unable to copy to clipboard", err);
    }
    document.body.removeChild(textArea);
  }

  useEffect(() => {
    if (property) {
      setDescription(property.description ? property.description : "");
    }
  }, [property]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const matchingEntry = data.find(entry => entry._id === property._id);
      if (matchingEntry && matchingEntry.totalCommission) {
        setCommission(matchingEntry.totalCommission.toFixed(2));
      } else {
        setCommission('0.00');
      }
    } else {
      setCommission('0.00');
    }
  }, [data, property._id]);

  // Get PMS badge info
  const getPMSBadge = () => {
    switch (property.propertySource) {
      case 'streamline':
        return { label: 'Streamline', color: 'bg-blue-100 text-blue-800' };
      case 'guesty':
        return { label: 'Guesty', color: 'bg-purple-100 text-purple-800' };
      default:
        return { label: 'Manual', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const pmsBadge = getPMSBadge();

  return (
    <>
      {threeDotBox && (
        <div
          onClick={() => {
            setThreeDotBox(false);
          }}
          className={`absolute opacity-40 top-0 left-0 h-screen w-full bg-black z-10`}
        ></div>
      )}

      {/* Property Card */}
      <div
        className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
        onClick={() =>
          !editMode && navigate(`/properties/${property._id}`)
        }
      >
                {/* Property Image */}
                <div className="relative h-48 overflow-hidden">
                  {isLoading ? (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                        <p className="text-gray-600 font-medium text-sm">Loading image...</p>
                      </div>
                    </div>
                  ) : hasImage ? (
                    <img
                      src={imageUrl}
                      alt={enhancedProperty.title || property.address?.line1}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        console.log('❌ Image failed to load:', imageUrl);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  {/* Fallback placeholder - shown when no image or image fails to load */}
                  <div 
                    className={`w-full h-full bg-gradient-to-br from-primary-100 via-primary-200 to-primary-300 flex items-center justify-center ${hasImage ? 'hidden' : ''}`}
                  >
                    <div className="text-center">
                      <svg className="w-12 h-12 text-primary-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <p className="text-primary-700 font-medium text-sm">Property Image</p>
                    </div>
                  </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          
          {/* PMS Badge */}
          <div className="absolute top-3 right-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${pmsBadge.color}`}>
              {pmsBadge.label}
            </span>
          </div>

          {/* Property Title Overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <h3 className="text-white font-semibold text-lg mb-1">
              {enhancedProperty.title || property.listingTitle || `${property.address?.line1}, ${property.address?.city}`}
            </h3>
            <p className="text-white/90 text-sm">
              {property.address?.line1}, {property.address?.city}, {property.address?.state}
            </p>
          </div>
        </div>

        {/* Property Details */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              {/* Property Stats */}
              {enhancedProperty.pmsData && (
                <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
                  {enhancedProperty.bedrooms && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      </svg>
                      <span>{enhancedProperty.bedrooms} beds</span>
                    </div>
                  )}
                  {enhancedProperty.bathrooms && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                      <span>{enhancedProperty.bathrooms} baths</span>
                    </div>
                  )}
                  {enhancedProperty.guests && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>{enhancedProperty.guests} guests</span>
                    </div>
                  )}
                </div>
              )}

              {/* Entry Instructions */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Entry Instructions
                  </h4>
                  {!editMode ? (
                    <button
                      className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                      onClick={(e) => {
                        setEditMode(true);
                        inputRef.current.focus();
                        e.stopPropagation();
                      }}
                    >
                      <img
                        className="w-4 h-4"
                        src={newedit}
                        alt="Edit"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        handleKeyUpdate();
                        setEditMode(false);
                        e.stopPropagation();
                      }}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Update
                    </button>
                  )}
                </div>
                
                <div className="relative">
                  <input
                    ref={inputRef}
                    name="KeyLocation"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={`w-full px-3 py-2 text-sm font-medium text-gray-900 bg-transparent border rounded-lg transition-all ${
                      editMode
                        ? "border-primary-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        : "border-transparent hover:border-gray-200"
                    }`}
                    readOnly={!editMode}
                    placeholder="Add entry instructions..."
                  />
                </div>
              </div>
            </div>

            {/* Earnings Section */}
            <div className="ml-6 text-center">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                Property Earnings
              </h4>
              <div className="bg-gradient-to-r from-success-50 to-success-100 rounded-xl p-4 border border-success-200">
                <div className="flex items-center justify-center space-x-1">
                  <svg className="w-4 h-4 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <span className="text-lg font-bold text-success-700">${commission}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* Copy Unique Shopping Link */}
            <button
              className="flex items-center space-x-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group flex-1"
              onClick={(e) => {
                unsecuredCopyToClipboard(property.uniqueLink, "");
                setCopyText("Copied!");
                setTimeout(() => {
                  setCopyText("Unique Shopping Link");
                }, 3000);
                e.stopPropagation();
              }}
            >
              <span className={`text-sm font-medium transition-colors ${
                copyText === "Copied!" ? "text-primary-500" : "text-gray-600"
              }`}>
                {copyText}
              </span>
              <img
                src={copy}
                alt="Copy"
                className="w-4 h-4 group-hover:scale-110 transition-transform"
              />
            </button>

            {/* Copy Message */}
            <button
              className="flex items-center space-x-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group flex-1"
              onClick={(e) => {
                const description = `I'm always looking for ways to elevate your stay, and I think you'll love this! We've partnered with Bellhopt to offer affordable pre-arrival grocery delivery.
                No more initial store runs; let's get your vacation started on the right foot! The best part? No need to create an account.
                Simply order directly to your rental here: `;
                unsecuredCopyToClipboard(property.uniqueLink, description);
                setCopyDescriptionText("Copied!");
                setTimeout(() => {
                  setCopyDescriptionText("Message");
                }, 3000);
                e.stopPropagation();
              }}
            >
              <span className={`text-sm font-medium transition-colors ${
                copyDescriptionText === "Copied!" ? "text-primary-500" : "text-gray-600"
              }`}>
                {copyDescriptionText}
              </span>
              <img
                src={copy}
                alt="Copy"
                className="w-4 h-4 group-hover:scale-110 transition-transform"
              />
            </button>
          </div>

          {/* Mobile Three Dot Menu */}
          <div className="lg:hidden mt-4 flex justify-center">
            <div
              className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              onClick={(e) => {
                handleMore(e);
              }}
            >
              <img
                src={more}
                className="w-5 h-5"
                alt="More options"
              />
              {threeDotBox && (
                <TripleDotComponent
                  fun={unsecuredCopyToClipboard}
                  propertyId={property._id}
                  ulink={property.uniqueLink}
                  keyInfo={property.description}
                  setCopyText={setCopyText}
                  setThreeDotBox={setThreeDotBox}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Property;